import { useState, useMemo, Fragment } from "react";
import { Card, CardContent } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { Badge } from "@/frontend/components/ui/badge";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/frontend/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/frontend/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/frontend/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, Loader2, Copy, ChevronRight, ChevronDown, Home } from "lucide-react";
import { RoomTypeFormFields, type RoomTypeFieldsData } from "@/frontend/components/admin/RoomTypeFormFields";
import { StatusBadge } from "@/frontend/components/admin/StatusBadge";
import { RoomType } from "@/shared/types";
import { isDraft } from "@/backend/lib/residenceUtils";
import { validateRoomDraft, validateRoomPublish } from "@/backend/lib/adminValidation";
import { useToast } from "@/backend/hooks/use-toast";
import { useAdminRoomTypes, useCreateRoomType, useUpdateRoomType, useDeleteRoomTypesSafely, useDuplicateRoomType, useRelabelTypology } from "@/backend/hooks/admin/useAdminRoomTypes";
import { useAdminResidences } from "@/backend/hooks/admin/useAdminResidences";

/** A table entry: a standalone room or a collapsible typology of identical units. */
type RoomGroup =
  | { kind: "single"; room: RoomType }
  | { kind: "typology"; gkey: string; typologyKey: string; units: RoomType[] };

type DeleteTarget =
  | { kind: "room"; room: RoomType }
  | { kind: "typology"; typologyKey: string; units: RoomType[] };

// True when an outside-interaction came from a portaled Radix popper (Select
// dropdown, combobox) rather than the modal overlay — so picking an option does
// not close the editor.
const clickIsInsidePopper = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  return !!el?.closest?.('[data-radix-popper-content-wrapper],[role="listbox"]');
};

// Stable snapshot of the room form (fields + unit count) for dirty-detection, so
// a no-op Save can be disabled. `status` is omitted — it's chosen by the button,
// not edited in the form.
const serializeRoomForm = (rt: Partial<RoomType>, unitCount: number): string =>
  JSON.stringify({
    residenceId: rt.residenceId ?? "",
    name: rt.name ?? "",
    description: rt.description ?? "",
    priceByOccupancy: rt.priceByOccupancy ?? [],
    maxOccupancy: rt.maxOccupancy ?? 1,
    area: rt.area ?? 0,
    floorPlanUrl: rt.floorPlanUrl ?? "",
    imagesUrl: (rt.imagesUrl ?? []).filter((u) => u.trim() !== ""),
    minStay: rt.minStay ?? 1,
    pricePerExcessKwh: rt.pricePerExcessKwh ?? null,
    typologyKey: rt.typologyKey ?? "",
    unitCount,
  });

const AdminRooms = () => {
  const { toast } = useToast();
  const { data: roomTypes = [], isLoading: roomTypesLoading } = useAdminRoomTypes();
  const { data: residences = [], isLoading: residencesLoading } = useAdminResidences();
  const createRoomType = useCreateRoomType();
  const updateRoomType = useUpdateRoomType();
  const deleteRoomTypesSafely = useDeleteRoomTypesSafely();
  const duplicateRoomType = useDuplicateRoomType();
  const relabelTypology = useRelabelTypology();

  const [filterResidence, setFilterResidence] = useState<string>("all");
  const [isResidenceSelectDialogOpen, setIsResidenceSelectDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  // Confirm prompt shown when closing the room editor with unsaved edits.
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null);
  const [roomToDuplicate, setRoomToDuplicate] = useState<RoomType | null>(null);
  const [duplicateCount, setDuplicateCount] = useState<number>(1);
  const [selectedResidenceId, setSelectedResidenceId] = useState<string>("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [newRoomId, setNewRoomId] = useState<string>("");
  const [unitCount, setUnitCount] = useState<number>(1);
  const [originalUnitCount, setOriginalUnitCount] = useState<number>(1);
  const [isRemoveUnitsDialogOpen, setIsRemoveUnitsDialogOpen] = useState(false);
  // Whether the in-flight save publishes or drafts the room. Held in state so the
  // remove-units confirmation dialog, which calls performSave() with no args,
  // preserves the intent chosen on the Save draft / Publish button.
  const [pendingStatus, setPendingStatus] = useState<"draft" | "published">("published");
  // Which save action is in flight, so only the clicked button shows a spinner.
  const [savingAction, setSavingAction] = useState<"draft" | "publish" | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [formData, setFormData] = useState<Partial<RoomType>>({
    residenceId: "",
    name: "",
    description: "",
    priceByOccupancy: [0],
    maxOccupancy: 1,
    area: 0,
    floorPlanUrl: "",
    imagesUrl: [],
    minStay: 1,
    pricePerExcessKwh: undefined,
  });

  const filteredRoomTypes = filterResidence === "all"
    ? roomTypes
    : roomTypes.filter((r) => r.residenceId === filterResidence);

  // Group rooms: typology rooms collapse into one entry; others stay standalone.
  const roomGroups = useMemo<RoomGroup[]>(() => {
    const groups: RoomGroup[] = [];
    const indexByKey = new Map<string, number>();
    for (const room of filteredRoomTypes) {
      const typologyKey = room.typologyKey?.trim();
      if (typologyKey) {
        const gkey = `${room.residenceId}|${typologyKey}`;
        const at = indexByKey.get(gkey);
        if (at !== undefined) {
          (groups[at] as Extract<RoomGroup, { kind: "typology" }>).units.push(room);
        } else {
          indexByKey.set(gkey, groups.length);
          groups.push({ kind: "typology", gkey, typologyKey, units: [room] });
        }
      } else {
        groups.push({ kind: "single", room });
      }
    }
    // Stable per-unit ordering (#1, #2, …) by creation time.
    for (const group of groups) {
      if (group.kind === "typology") {
        group.units.sort(
          (a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0)
        );
      }
    }
    return groups;
  }, [filteredRoomTypes]);

  const toggleGroup = (gkey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gkey)) {
        next.delete(gkey);
      } else {
        next.add(gkey);
      }
      return next;
    });
  };

  const isRoomMutating =
    createRoomType.isPending ||
    updateRoomType.isPending ||
    deleteRoomTypesSafely.isPending ||
    relabelTypology.isPending ||
    isImageUploading ||
    savingAction !== null;

  // Has the room form changed since it was loaded? A new room (no selected room)
  // is always dirty. Used to disable no-op saves.
  const isRoomDirty =
    !selectedRoomType ||
    serializeRoomForm(formData, unitCount) !==
      serializeRoomForm(selectedRoomType, originalUnitCount);
  const currentRoomStatus = selectedRoomType?.status ?? "published";
  const isPublishDisabled =
    isRoomMutating ||
    (!!selectedRoomType && currentRoomStatus === "published" && !isRoomDirty);
  const isDraftDisabled =
    isRoomMutating ||
    (!!selectedRoomType && currentRoomStatus === "draft" && !isRoomDirty);

  const priceLabel = (rt: RoomType) => {
    // A draft (or any room without valid prices) has no price to show — avoid
    // rendering €undefined / €Infinity.
    if (isDraft(rt) || !rt.priceByOccupancy?.length || rt.priceByOccupancy.some((p) => !p || p <= 0)) {
      return "Draft · no price";
    }
    return rt.maxOccupancy === 1
      ? `€${rt.priceByOccupancy[0]}/mo`
      : `from €${Math.min(...rt.priceByOccupancy)}/mo`;
  };

  const openAddModal = () => {
    setSelectedRoomType(null);
    setSelectedResidenceId("");
    setIsResidenceSelectDialogOpen(true);
  };

  const openAddModalForResidence = (residenceId: string) => {
    setSelectedRoomType(null);
    setNewRoomId(crypto.randomUUID());
    setUnitCount(1);
    setOriginalUnitCount(1);
    setSelectedResidenceId(residenceId);
    setFormData({
      residenceId: residenceId,
      name: "",
      description: "",
      priceByOccupancy: [0],
      maxOccupancy: 1,
      area: 0,
      floorPlanUrl: "",
      imagesUrl: [],
      minStay: 1,
      pricePerExcessKwh: undefined,
    });
    setIsResidenceSelectDialogOpen(false);
    setIsModalOpen(true);
  };

  const openEditModal = (roomType: RoomType) => {
    setSelectedRoomType(roomType);
    setSelectedResidenceId(roomType.residenceId);
    setFormData({ ...roomType });
    // Pre-fill the units field with the typology's current size.
    const currentUnits = roomType.typologyKey
      ? roomTypes.filter(
          (r) => r.residenceId === roomType.residenceId && r.typologyKey === roomType.typologyKey
        ).length
      : 1;
    setUnitCount(currentUnits);
    setOriginalUnitCount(currentUnits);
    setIsModalOpen(true);
  };

  const openDeleteDialog = (roomType: RoomType) => {
    setDeleteTarget({ kind: "room", room: roomType });
    setIsDeleteDialogOpen(true);
  };

  const openDeleteTypologyDialog = (typologyKey: string, units: RoomType[]) => {
    setDeleteTarget({ kind: "typology", typologyKey, units });
    setIsDeleteDialogOpen(true);
  };

  const openDuplicateDialog = (roomType: RoomType) => {
    setRoomToDuplicate(roomType);
    setDuplicateCount(1);
    setIsDuplicateDialogOpen(true);
  };

  const closeRoomModal = () => {
    setIsModalOpen(false);
    setSelectedResidenceId("");
    setSelectedRoomType(null);
  };

  const handleSave = async (publish: boolean) => {
    // Draft needs only a residence + name; publishing runs the full validation.
    const error = publish ? validateRoomPublish(formData) : validateRoomDraft(formData);
    if (error) {
      toast({ title: "Error", description: error, variant: "destructive" });
      return;
    }

    setPendingStatus(publish ? "published" : "draft");

    // Editing a typology and lowering the unit count → confirm the removal first.
    if (
      selectedRoomType &&
      formData.typologyKey &&
      formData.typologyKey === selectedRoomType.typologyKey &&
      Math.max(1, unitCount) < originalUnitCount
    ) {
      setIsRemoveUnitsDialogOpen(true);
      return;
    }

    await performSave(publish ? "published" : "draft");
  };

  // Commits the room form: updates/creates the room and adjusts the typology's
  // unit count. Called directly with a status, or by the remove-units
  // confirmation dialog (no args → falls back to the pending status).
  const performSave = async (statusArg?: "draft" | "published") => {
    const status = statusArg ?? pendingStatus;
    const filteredImages = (formData.imagesUrl || []).filter((url) => url.trim() !== "");
    setSavingAction(status === "published" ? "publish" : "draft");
    try {
      if (selectedRoomType) {
        const { syncedCount } = await updateRoomType.mutateAsync({
          id: selectedRoomType.id,
          ...formData,
          imagesUrl: filteredImages,
          status,
        });

        const oldKey = selectedRoomType.typologyKey;
        const newKey = formData.typologyKey;
        let description = syncedCount > 0
          ? `Room updated: ${syncedCount} other unit${syncedCount > 1 ? "s" : ""} synced`
          : "Room updated successfully";

        if (oldKey && oldKey !== newKey) {
          // The typology was renamed / merged / dissolved — move the whole group,
          // not just the edited room.
          const oldMembers = roomTypes.filter(
            (r) =>
              r.residenceId === selectedRoomType.residenceId &&
              r.typologyKey === oldKey &&
              r.id !== selectedRoomType.id
          );
          const { moved } = await relabelTypology.mutateAsync({
            memberIds: oldMembers.map((m) => m.id),
            toKey: newKey,
            sharedFields: { ...formData, imagesUrl: filteredImages },
            residenceId: selectedRoomType.residenceId,
          });
          const total = moved + 1;
          description = newKey
            ? `Typology moved: ${total} unit${total > 1 ? "s" : ""} now in "${newKey}"`
            : `Typology "${oldKey}" dissolved: ${total} standalone room${total > 1 ? "s" : ""}`;
        } else if (newKey) {
          // Same typology (or a standalone room joining one) — adjust the unit count.
          const delta = Math.max(1, unitCount) - originalUnitCount;
          if (delta > 0) {
            await createRoomType.mutateAsync({
              ...formData,
              id: undefined,
              imagesUrl: filteredImages,
              count: delta,
              status,
            } as Omit<RoomType, 'id' | 'createdAt' | 'updatedAt'> & { count: number });
            description += ` · ${delta} unit${delta > 1 ? "s" : ""} added`;
          } else if (delta < 0) {
            const siblings = roomTypes
              .filter(
                (r) =>
                  r.residenceId === selectedRoomType.residenceId &&
                  r.typologyKey === selectedRoomType.typologyKey &&
                  r.id !== selectedRoomType.id
              )
              .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
            const { removed, keptDueToBookings } = await deleteRoomTypesSafely.mutateAsync({
              candidateIds: siblings.map((s) => s.id),
              max: -delta,
              residenceId: selectedRoomType.residenceId,
            });
            description += ` · ${removed} unit${removed !== 1 ? "s" : ""} removed`;
            if (keptDueToBookings > 0) {
              description += `, ${keptDueToBookings} kept (has bookings)`;
            }
          }
        }

        toast({ title: "Success", description });
      } else {
        const createdCount = formData.typologyKey ? Math.max(1, unitCount) : 1;
        const { syncedCount } = await createRoomType.mutateAsync({
          id: newRoomId,
          ...formData,
          imagesUrl: filteredImages,
          count: createdCount,
          status,
        } as Omit<RoomType, 'id' | 'createdAt' | 'updatedAt'> & { id: string; count: number });
        const base = createdCount > 1 ? `${createdCount} rooms added` : "Room added";
        toast({
          title: "Success",
          description: syncedCount > 0
            ? `${base}: ${syncedCount} existing unit${syncedCount > 1 ? "s" : ""} synced`
            : `${base} successfully`,
        });
      }
      setIsModalOpen(false);
      setIsRemoveUnitsDialogOpen(false);
      setSelectedResidenceId("");
      setSelectedRoomType(null);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save room. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSavingAction(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.kind === "room") {
        const { removed } = await deleteRoomTypesSafely.mutateAsync({
          candidateIds: [deleteTarget.room.id],
          residenceId: deleteTarget.room.residenceId,
        });
        if (removed > 0) {
          toast({ title: "Success", description: "Room deleted successfully" });
        } else {
          toast({
            title: "Cannot delete",
            description: "This room has bookings and was kept.",
            variant: "destructive",
          });
        }
      } else {
        const { removed, keptDueToBookings } = await deleteRoomTypesSafely.mutateAsync({
          candidateIds: deleteTarget.units.map((u) => u.id),
          residenceId: deleteTarget.units[0]?.residenceId,
        });
        toast({
          title: removed > 0 ? "Success" : "Cannot delete",
          description: keptDueToBookings > 0
            ? `Removed ${removed} unit${removed !== 1 ? "s" : ""}; ${keptDueToBookings} kept (have bookings)`
            : `Typology "${deleteTarget.typologyKey}" deleted (${removed} units)`,
          variant: removed > 0 ? undefined : "destructive",
        });
      }
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = async () => {
    if (!roomToDuplicate) return;
    const count = Math.max(1, Math.min(20, duplicateCount));
    try {
      await duplicateRoomType.mutateAsync({ id: roomToDuplicate.id, count });
      toast({ title: "Success", description: `${count} room${count > 1 ? 's' : ''} duplicated successfully` });
      setIsDuplicateDialogOpen(false);
      setRoomToDuplicate(null);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to duplicate room. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (roomTypesLoading || residencesLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <Select value={filterResidence} onValueChange={setFilterResidence}>
          <SelectTrigger className="w-full md:w-80">
            <SelectValue placeholder="Filter by residence" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Residences</SelectItem>
            {residences.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={openAddModal}>
          <Plus className="h-4 w-4 mr-2" />
          Add Room
        </Button>
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Residence</TableHead>
                <TableHead>Base Price</TableHead>
                <TableHead>Max Occupancy</TableHead>
                <TableHead>Excess kWh €</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roomGroups.map((group) => {
                if (group.kind === "single") {
                  const roomType = group.room;
                  const residence = residences.find((r) => r.id === roomType.residenceId);
                  return (
                    <TableRow key={roomType.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{roomType.name}</span>
                          {isDraft(roomType) && <StatusBadge status="draft" />}
                        </div>
                      </TableCell>
                      <TableCell>{residence?.name || "Unknown"}</TableCell>
                      <TableCell>{priceLabel(roomType)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {roomType.maxOccupancy}
                        </div>
                      </TableCell>
                      <TableCell>
                        {roomType.pricePerExcessKwh != null
                          ? `€${roomType.pricePerExcessKwh}/kWh`
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openDuplicateDialog(roomType)} title="Duplicate room">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(roomType)} title="Edit room">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => openDeleteDialog(roomType)} title="Delete room">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                }

                const rep = group.units[0];
                const residence = residences.find((r) => r.id === rep.residenceId);
                const expanded = expandedGroups.has(group.gkey);
                const unitsLabel = `${group.units.length} unit${group.units.length > 1 ? "s" : ""}`;
                return (
                  <Fragment key={group.gkey}>
                    <TableRow className="bg-muted/40 hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.gkey)}
                          className="flex items-center gap-2 text-left"
                        >
                          {expanded
                            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          <span>{rep.name}</span>
                          <Badge variant="secondary" className="gap-1 bg-secondary/10 text-secondary">
                            <Home className="h-3 w-3" />
                            {group.typologyKey} · {unitsLabel}
                          </Badge>
                          {group.units.some(isDraft) && <StatusBadge status="draft" />}
                        </button>
                      </TableCell>
                      <TableCell>{residence?.name || "Unknown"}</TableCell>
                      <TableCell>{priceLabel(rep)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {rep.maxOccupancy}
                        </div>
                      </TableCell>
                      <TableCell>
                        {rep.pricePerExcessKwh != null
                          ? `€${rep.pricePerExcessKwh}/kWh`
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(rep)} title="Edit typology">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => openDeleteTypologyDialog(group.typologyKey, group.units)} title="Delete typology">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded && group.units.map((unit, i) => (
                      <TableRow key={unit.id} className="text-muted-foreground">
                        <TableCell className="pl-12">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                              {i + 1}
                            </span>
                            {isDraft(unit) && <StatusBadge status="draft" />}
                          </div>
                        </TableCell>
                        <TableCell>{residence?.name || "Unknown"}</TableCell>
                        <TableCell>{priceLabel(unit)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {unit.maxOccupancy}
                          </div>
                        </TableCell>
                        <TableCell>
                          {unit.pricePerExcessKwh != null ? `€${unit.pricePerExcessKwh}/kWh` : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => openDeleteDialog(unit)} title="Delete this unit">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {roomGroups.map((group) => {
          if (group.kind === "single") {
            const roomType = group.room;
            const residence = residences.find((r) => r.id === roomType.residenceId);
            return (
              <Card key={roomType.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{roomType.name}</p>
                    {isDraft(roomType) && <StatusBadge status="draft" className="shrink-0" />}
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Residence</span>
                      <span className="text-right truncate">{residence?.name || "Unknown"}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Base Price</span>
                      <span>{priceLabel(roomType)}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Max Occupancy</span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {roomType.maxOccupancy}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Excess kWh</span>
                      <span>
                        {roomType.pricePerExcessKwh != null
                          ? `€${roomType.pricePerExcessKwh}/kWh`
                          : <span className="text-muted-foreground">—</span>}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-3 border-t">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openDuplicateDialog(roomType)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(roomType)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="outline" size="icon" className="shrink-0 text-destructive" onClick={() => openDeleteDialog(roomType)} title="Delete room">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          }

          const rep = group.units[0];
          const residence = residences.find((r) => r.id === rep.residenceId);
          const expanded = expandedGroups.has(group.gkey);
          const unitsLabel = `${group.units.length} unit${group.units.length > 1 ? "s" : ""}`;
          return (
            <Card key={group.gkey} className="bg-muted/30">
              <CardContent className="p-4 space-y-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.gkey)}
                  className="flex w-full items-start gap-2 text-left"
                >
                  {expanded
                    ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />}
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{rep.name}</p>
                      {group.units.some(isDraft) && <StatusBadge status="draft" className="shrink-0" />}
                    </div>
                    <Badge variant="secondary" className="gap-1 bg-secondary/10 text-secondary whitespace-nowrap">
                      <Home className="h-3 w-3 shrink-0" />
                      {group.typologyKey} · {unitsLabel}
                    </Badge>
                  </div>
                </button>

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Residence</span>
                    <span className="text-right truncate">{residence?.name || "Unknown"}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Base Price</span>
                    <span>{priceLabel(rep)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Max Occupancy</span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {rep.maxOccupancy}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Excess kWh</span>
                    <span>
                      {rep.pricePerExcessKwh != null
                        ? `€${rep.pricePerExcessKwh}/kWh`
                        : <span className="text-muted-foreground">—</span>}
                    </span>
                  </div>
                </div>

                {expanded && (
                  <div className="space-y-2 border-t pt-3">
                    {group.units.map((unit, i) => (
                      <div key={unit.id} className="rounded-md border bg-background p-3 space-y-1 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                            {i + 1}
                          </span>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => openDeleteDialog(unit)} title="Delete this unit">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex justify-between gap-2 text-muted-foreground">
                          <span>Base Price</span>
                          <span>{priceLabel(unit)}</span>
                        </div>
                        <div className="flex justify-between gap-2 text-muted-foreground">
                          <span>Max Occupancy</span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {unit.maxOccupancy}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2 text-muted-foreground">
                          <span>Excess kWh</span>
                          <span>
                            {unit.pricePerExcessKwh != null ? `€${unit.pricePerExcessKwh}/kWh` : "—"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(rep)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button variant="outline" size="icon" className="shrink-0 text-destructive" onClick={() => openDeleteTypologyDialog(group.typologyKey, group.units)} title="Delete typology">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Residence Selection Dialog */}
      <Dialog
        open={isResidenceSelectDialogOpen}
        onOpenChange={(open) => {
          setIsResidenceSelectDialogOpen(open);
          if (!open) {
            setSelectedResidenceId("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Residence</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label>Choose a residence to add a room to</Label>
              <Select
                value={selectedResidenceId}
                onValueChange={setSelectedResidenceId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select residence" />
                </SelectTrigger>
                <SelectContent>
                  {residences.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResidenceSelectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedResidenceId) {
                  openAddModalForResidence(selectedResidenceId);
                }
              }}
              disabled={!selectedResidenceId}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsModalOpen(true);
            return;
          }
          // Closing via overlay click / Esc / X — confirm first if there are
          // unsaved edits, otherwise close immediately.
          if (isRoomDirty) {
            setIsDiscardDialogOpen(true);
            return;
          }
          closeRoomModal();
        }}
      >
        <DialogContent
          className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            if (clickIsInsidePopper(e.target)) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{selectedRoomType ? "Edit Room" : "Add Room"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="residenceId">Residence *</Label>
              <Select
                value={formData.residenceId || ""}
                onValueChange={(value) => setFormData({ ...formData, residenceId: value })}
                disabled={!!selectedResidenceId || !!selectedRoomType}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select residence" />
                </SelectTrigger>
                <SelectContent>
                  {residences.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(selectedResidenceId || selectedRoomType) && (
                <p className="text-xs text-muted-foreground">
                  Residence cannot be changed when editing a room
                </p>
              )}
            </div>

            <RoomTypeFormFields
              data={formData as RoomTypeFieldsData}
              onChange={(d) => setFormData((prev) => ({ ...prev, ...d }))}
              roomId={selectedRoomType?.id || newRoomId}
              onUploadingChange={setIsImageUploading}
              residenceRooms={roomTypes.filter((r) => r.residenceId === formData.residenceId)}
              unitCount={unitCount}
              onUnitCountChange={setUnitCount}
              isEditing={!!selectedRoomType}
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => (isRoomDirty ? setIsDiscardDialogOpen(true) : closeRoomModal())}
              disabled={isRoomMutating}
            >
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => handleSave(false)} disabled={isDraftDisabled}>
              {savingAction === "draft" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save draft"
              )}
            </Button>
            <Button onClick={() => handleSave(true)} disabled={isPublishDisabled}>
              {savingAction === "publish" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Publish"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog
        open={isDuplicateDialogOpen}
        onOpenChange={(open) => {
          setIsDuplicateDialogOpen(open);
          if (!open) setRoomToDuplicate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Room</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Duplicating <span className="font-medium text-foreground">"{roomToDuplicate?.name}"</span>.
              Each copy will be named with a sequential number suffix (e.g., "{roomToDuplicate?.name} 1", "{roomToDuplicate?.name} 2").
            </p>
            <div className="space-y-2">
              <Label htmlFor="duplicateCount">Number of copies</Label>
              <Input
                id="duplicateCount"
                type="number"
                min={1}
                max={20}
                value={duplicateCount}
                onChange={(e) => setDuplicateCount(Math.max(1, Math.min(20, Number(e.target.value))))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicateDialogOpen(false)} disabled={duplicateRoomType.isPending}>
              Cancel
            </Button>
            <Button onClick={handleDuplicate} disabled={duplicateRoomType.isPending}>
              {duplicateRoomType.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Duplicating...
                </>
              ) : (
                "Duplicate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === "typology" ? "Delete Typology" : "Delete Room"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "typology"
                ? `Delete typology "${deleteTarget.typologyKey}" and its ${deleteTarget.units.length} unit${deleteTarget.units.length > 1 ? "s" : ""}? Units with active bookings will be kept. This cannot be undone.`
                : `Delete "${deleteTarget?.room.name ?? ""}"? A room with active bookings cannot be deleted. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRoomTypesSafely.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteRoomTypesSafely.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteRoomTypesSafely.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Units Confirmation */}
      <AlertDialog open={isRemoveUnitsDialogOpen} onOpenChange={setIsRemoveUnitsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove units</AlertDialogTitle>
            <AlertDialogDescription>
              This reduces typology "{selectedRoomType?.typologyKey}" from {originalUnitCount} to {unitCount} unit{unitCount > 1 ? "s" : ""}, removing the {originalUnitCount - unitCount} most recently created. Units with active bookings are kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateRoomType.isPending || deleteRoomTypesSafely.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={performSave}
              disabled={updateRoomType.isPending || deleteRoomTypesSafely.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(updateRoomType.isPending || deleteRoomTypesSafely.isPending) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove units"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard unsaved changes confirmation */}
      <AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you close now they will be lost. Use Save draft
              to keep your progress.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsDiscardDialogOpen(false);
                closeRoomModal();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRooms;
